import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import ReactGA4 from 'react-ga4'
import ReactPixel from 'react-facebook-pixel'
import { stripEmptyFromObject } from '@one-tree/library'
import { IOrganisationResponse } from '../types/organisationTypes'
import { atHome } from '../helpers/homeHelper'
import { useManualOrder } from '../context/ManualOrderProvider'

// NOTE: If the user has an ad blocker installed, analytics will fail silently

// NOTE: Do not enable ONE_TREE_ID without implementing <CookieBanner /> in App.tsx
const ONE_TREE_ID = process.env.REACT_APP_ENABLE_ANALYTICS_WITH_ID

const host = process.env.REACT_APP_API_HOST
const IS_STAGING = host && host.includes('one-tree.dev/api')

export function useAnalytics({ org }: { org?: IOrganisationResponse }): void {
  const { gaTrackingId, fbPixelId, name } = org || {}
  const { manualOrderToken } = useManualOrder()
  const location = useLocation()

  const noAnalytics = !org || manualOrderToken || IS_STAGING

  // Initialize analytics
  useEffect(() => {
    if (noAnalytics) return

    ReactGA4.initialize([
      { trackingId: ONE_TREE_ID || '' },
      { trackingId: gaTrackingId || '' },
    ])
    if (fbPixelId) ReactPixel.init(fbPixelId)
  }, [noAnalytics])

  // Send pageview/ViewContent events
  useEffect(() => {
    if (noAnalytics) return

    const page = atHome(location.pathname) ? '/home' : location.pathname

    ReactGA4.send({
      hitType: 'pageview',
      page,
      // NOTE: can add this if we want to implement ONE_TREE_ID:
      // page_title: `${name}: ${page}`,
      label: name,
    })

    if (fbPixelId) ReactPixel.track('ViewContent', { page })
  }, [location.pathname, noAnalytics])
}

// Helper to fire generic events
export const analyticsEvent = (
  event: [string, string],
  organisation?: IOrganisationResponse,
  payload?: Object,
): void => {
  if (!organisation || IS_STAGING) return
  const { gaTrackingId, fbPixelId } = organisation

  const object = stripEmptyFromObject(payload || {})

  if (gaTrackingId || ONE_TREE_ID) ReactGA4.event(event[0], object)
  if (fbPixelId) ReactPixel.track(event[1], object)
}
